import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import Layout from './Layout'
import GridContainer from './GridContainer'
import GridCard from './GridCard'
import CardHeader from './CardHeader'
import { LoadingDotsIcon } from './Icons'
import AButton from './AButton'
import SubdirectoryArrowLeftIcon from '@mui/icons-material/SubdirectoryArrowLeft'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { MessageContext } from './Contexts'
import { BillingListDataRooms, BillingUserInfo, BillingResetPassword } from 'modules/Rest'
import { DirectActionButton } from './ActionButtons'

const useStyles = makeStyles(() => ({
  loadingDots: {
    paddingBottom: 10,
    paddingLeft: 10,
    display: 'inline-block',
  },
  btnCell: {
    textAlign: 'right',
    paddingRight: 0,
    '&:last-child': {
      paddingRight: 0,
    },
    paddingTop: 1,
    paddingBottom: 1,
  },
  headingCell: {
    fontWeight: 500,
  },
  showMoreContainer: {
    marginTop: 15,
    textAlign: 'center',
  },
  wMargin: {
    marginTop: 25,
  },
  noData: {
    fontStyle: 'italic',
  },
}))

export default function BillingUser() {
  const { userName = '' } = useParams()
  const classes = useStyles()
  const navigate = useNavigate()
  const { messageDispatch } = useContext(MessageContext)
  const [loadingDataRooms, setLoadingDataRooms] = useState(false)
  const [dataRooms, setDataRooms] = useState([])
  const [userInfo, setUserInfo] = useState(null)

  const getDataRooms = useCallback(async (prevDataRooms=[], cursor=null) => {
    try {
      setLoadingDataRooms(true)
      const { data: { success, info, data_rooms, cursor: newCursor } } = await BillingListDataRooms({cursor, user_name: userName})
      if (success) {
        const newDataRooms = [...prevDataRooms, ...data_rooms]
          .sort((a, b) => a.data_room_display_name.localeCompare(b.data_room_display_name, 'sv', { numeric: true, sensitivity: 'base' }))
        setDataRooms(newDataRooms)

        if (newCursor) {
          await getDataRooms(newDataRooms, newCursor)
        }
      } else {
        throw new Error(info ? info : 'An error occurred')
      }
    } catch (error) {
      messageDispatch({ type: 'warning', payload: error.message })
    }
    setLoadingDataRooms(false)
  }, [messageDispatch, userName])

  useEffect(() => {
    getDataRooms()
  }, [getDataRooms])

  const getUserInfo = useCallback(async () => {
      try {
        const { data: { success, info, ...data } } = await BillingUserInfo({user_name: userName})
        if (success) {
          setUserInfo(data)
        } else {
          throw new Error(info ? info : 'An error occurred')
        }
      } catch (error) {
        messageDispatch({ type: 'warning', payload: error.message })
      }
  }, [messageDispatch, userName])

  useEffect(() => {
    getUserInfo()
  }, [getUserInfo])

  const forcePasswordReset = useCallback(async (user_name) => {
      try {
        const { data: { success, info } } = await BillingResetPassword({user_name})
        if (success) {
          messageDispatch({type: 'info', payload: 'The action has been triggered. The user will be required to change their password the next time they log in.'})
        } else {
          throw new Error(info ? info : 'An error occurred')
        }
      } catch (error) {
        messageDispatch({ type: 'warning', payload: error.message })
      }
  }, [messageDispatch])

  return <Layout showRoomSelect={false}>
    <GridContainer>
      <GridCard sm={12} uniformPadding={true}>
        <CardHeader
          title={<>
            <>User Details</>
            {loadingDataRooms ? <div className={classes.loadingDots}><LoadingDotsIcon size={6} vPadding={6} hPadding={6}/></div> : null}
          </>}
          headingElement='h5'
          buttons={[<AButton
            startIcon={<SubdirectoryArrowLeftIcon/>}
            onClick={() => navigate(-1)}
            style={{ float: 'right' }}
          >
            Back
          </AButton>]}
        />

        {userInfo && <TableContainer>
          <Table size='small'>
            <TableBody>
              <TableRow>
                <TableCell className={classes.headingCell}>User name</TableCell>
                <TableCell>{userInfo.user_name}</TableCell>
                <TableCell/>
              </TableRow>
              <TableRow>
                <TableCell className={classes.headingCell}>Billing ID</TableCell>
                <TableCell>{userInfo.billing_id}</TableCell>
                <TableCell/>
              </TableRow>
              {userInfo.allow_password_reset && <TableRow>
                <TableCell className={classes.headingCell}>Force user to change the password</TableCell>
                <TableCell/>
                <TableCell className={classes.btnCell}>
                  <DirectActionButton actionTitle='Force password reset' action={() => forcePasswordReset(userInfo.user_name)} extraModes={['completing', 1500]}/>
                </TableCell>
              </TableRow>}
            </TableBody>
          </Table>
        </TableContainer>}

        {!loadingDataRooms || (dataRooms && dataRooms.length) ? <TableContainer>
          <Table size='small' className={classes.wMargin}>
            <TableHead>
              <TableRow className={classes.tableRow}>
                <TableCell>Data Room</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Billing ID</TableCell>
                <TableCell/>
              </TableRow>
            </TableHead>
            <TableBody>
              {dataRooms.length ? dataRooms.map((dataRoom) => (
                <TableRow key={dataRoom.data_room}>
                  <TableCell>
                    {dataRoom.data_room_display_name}
                    {dataRoom && dataRoom.light && <span style={{ fontStyle: 'italic' }}> Light</span>}
                  </TableCell>
                  <TableCell>
                    {dataRoom.deleted ? 'Deleted' : (dataRoom.disabled ? 'Disabled' : 'Enabled')}
                  </TableCell>
                  <TableCell>
                    {dataRoom.billing_id}
                  </TableCell>
                  <TableCell className={classes.btnCell}>
                    <AButton onClick={() => navigate(`/billing-data-room/${dataRoom.data_room}`)}>More info</AButton>
                  </TableCell>
                </TableRow>
              )) : <TableRow><TableCell className={classes.noData} colSpan={4}>No data rooms</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer> : null}
      </GridCard>
    </GridContainer>
  </Layout>
}
